import { supabase } from '../../lib/supabase';

// Single fetch for everything the Scraper Control page's top sections need:
// config, current status, the latest run, any active OTP request, and a
// short recent-run history. Kept as one function (rather than one query per
// section) because every section on this page reads about the same
// scraper_id and is shown together on first load -- there is no independent
// section here the way live-tender-info's blocks are independently paged.
export const fetchScraperOverview = async (source = 'ebuysugar') => {
  const { data: config, error: configError } = await supabase
    .from('scraper_configs')
    .select('id, source, display_name, enabled')
    .eq('source', source)
    .maybeSingle();

  if (configError) return { overview: null, error: configError.message };
  if (!config) return { overview: null, error: null };

  const [
    { data: status, error: statusError },
    { data: runs, error: runsError },
    { data: otpRequests, error: otpError },
    { data: manualRequests, error: manualError },
  ] = await Promise.all([
    supabase.from('scraper_status').select('*').eq('scraper_id', config.id).maybeSingle(),
    // Limit applied in the query, not just hidden in the browser -- table
    // rows themselves are never deleted/modified, only how many are fetched
    // for display. 45, newest first. No automatic schedule is actually
    // wired up anywhere yet -- GitHub Actions is workflow_dispatch-only,
    // render.yaml's cron schedule is an explicit placeholder never
    // deployed, and no scraper-related pg_cron job exists in the live
    // database (all runs today are manual, via Request Manual Run).
    supabase
      .from('scraper_runs')
      .select('*')
      .eq('scraper_id', config.id)
      .order('started_at', { ascending: false })
      .limit(45),
    supabase
      .from('scraper_otp_requests')
      .select('*')
      .eq('scraper_id', config.id)
      .in('status', ['PENDING', 'OTP_SUBMITTED'])
      .order('requested_at', { ascending: false })
      .limit(1),
    supabase
      .from('scraper_manual_run_requests')
      .select('*')
      .eq('scraper_id', config.id)
      .in('status', ['PENDING', 'CLAIMED'])
      .order('requested_at', { ascending: false })
      .limit(1),
  ]);

  const error = statusError?.message || runsError?.message || otpError?.message || manualError?.message || null;

  return {
    overview: {
      config,
      status: status || null,
      latestRun: runs?.[0] || null,
      recentRuns: runs || [],
      activeOtpRequest: otpRequests?.[0] || null,
      activeManualRequest: manualRequests?.[0] || null,
    },
    error,
  };
};

// Admin submits the OTP they were given. The value is sent once, over this
// one RPC call, and never stored/echoed back anywhere in this app -- see
// submit_scraper_otp in 20260821150000_scraper_control_schema.sql for the
// server-side guarantee (the value lives only in scraper_otp_secrets, a
// table with zero RLS policies for anon/authenticated).
export const submitScraperOtp = async (otpRequestId, otpValue) => {
  const { error } = await supabase.rpc('submit_scraper_otp', {
    p_otp_request_id: otpRequestId,
    p_otp_value: otpValue,
  });
  if (error) return { success: false, error: error.message };
  return { success: true, error: null };
};

// Queues a manual run request AND starts the cloud worker for it, via the
// scraper-request-manual-run edge function. The function itself calls the
// existing request_manual_scraper_run RPC first (unchanged -- same
// admin gate, same one-active-request-at-a-time guarantee) and only
// dispatches the GitHub Actions workflow if that RPC actually created a
// new request; if one was already queued, or the caller isn't admin, the
// RPC's own error comes back here and nothing is dispatched twice.
// `dispatched: true` means the cloud worker was actually started, not just
// that a request row was written.
export const requestManualScraperRun = async (scraperId) => {
  const { data, error } = await supabase.functions.invoke('scraper-request-manual-run', {
    body: { scraper_id: scraperId },
  });
  if (error) return { requestId: null, dispatched: false, error: error.message };
  if (!data?.success) {
    return { requestId: data?.requestId ?? null, dispatched: false, error: data?.error || 'Request failed' };
  }
  return { requestId: data.requestId, dispatched: data.dispatched, error: null };
};

// Masked eBuySugar login number (e.g. "9550******") for display only -- the
// full value is never sent to the browser. Returns null if none is
// configured yet. See get_scraper_login_number_masked in
// 20260821211952_scraper_dynamic_login_number.sql.
export const fetchScraperLoginNumberMasked = async (scraperId) => {
  const { data, error } = await supabase.rpc('get_scraper_login_number_masked', {
    p_scraper_id: scraperId,
  });
  if (error) return { masked: null, error: error.message };
  return { masked: data, error: null };
};

// Admin sets/changes the active eBuySugar login mobile number. Server-side
// validates the `^[6-9][0-9]{9}$` format and admin role -- see
// update_scraper_login_number in the same migration.
export const updateScraperLoginNumber = async (scraperId, mobileNumber) => {
  const { error } = await supabase.rpc('update_scraper_login_number', {
    p_scraper_id: scraperId,
    p_mobile_number: mobileNumber,
  });
  if (error) return { success: false, error: error.message };
  return { success: true, error: null };
};
